<!-- Placeholder view with icon, title, subtitle, and actions for empty content states. -->

<script lang="ts" setup>
import type { Component, VNodeChild } from 'vue';
import { computed } from 'vue';

type EmptyStateColor = 'blue' | 'green' | 'red' | 'yellow';

interface Props {
  icon?: Component | null;
  iconSize?: number;
  color?: EmptyStateColor;
}

interface Slots {
  title: () => VNodeChild;
  subtitle: () => VNodeChild;
  actions: () => VNodeChild;
}

const props = withDefaults(defineProps<Props>(), {
  iconSize: 64,
  color: 'blue',
});

defineSlots<Slots>();

const iconBgSize = computed(() => Math.round(props.iconSize * 2.15));
const iconBgRadius = computed(() => Math.round(iconBgSize.value * 0.375));
</script>

<template>
  <div :class="[css.root, css[`color-${color}`]]">
    <div
      v-if="icon"
      :class="css.iconBg"
      :style="{
        width: `${iconBgSize}px`,
        height: `${iconBgSize}px`,
        borderRadius: `${iconBgRadius}px`,
      }"
    >
      <component
        :is="icon"
        :class="css.icon"
        :size="iconSize"
      />
    </div>

    <div
      v-if="$slots.title || $slots.subtitle"
      :class="css.text"
    >
      <h2
        v-if="$slots.title"
        :class="css.title"
      >
        <slot name="title" />
      </h2>

      <p
        v-if="$slots.subtitle"
        :class="css.subtitle"
      >
        <slot name="subtitle" />
      </p>
    </div>

    <div
      v-if="$slots.actions"
      :class="css.actions"
    >
      <slot name="actions" />
    </div>
  </div>
</template>

<style lang="scss" module="css">
.root {
  --empty-accent: #{$color-doctor-600};
  --empty-icon-bg: #{$color-doctor-100};

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $spacing-20;
  text-align: center;
  padding-block: $spacing-32;
  margin-block: auto;
}

.color-yellow {
  --empty-accent: #{$color-yellow-700};
  --empty-icon-bg: #{$color-yellow-100};
}

.color-green {
  --empty-accent: #{$color-green-500};
  --empty-icon-bg: #{$color-green-100};
}

.color-red {
  --empty-accent: #{$color-red-600};
  --empty-icon-bg: #{$color-red-50};
}

.iconBg {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--empty-icon-bg);
  color: var(--empty-accent);
}

.icon {
  flex-shrink: 0;
}

.text {
  display: flex;
  flex-direction: column;
  gap: $spacing-08;
  max-width: 300px;
}

.title {
  font-size: $font-size-20;
  font-weight: 600;
}

.subtitle {
  font-size: $font-size-14;
  color: $color-gray-600;
  line-height: 1.5;
}

.actions {
  display: flex;
  align-items: center;
  gap: $spacing-12;
}
</style>
